import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  FilingSendStatusDto,
  FilingSendResultDto,
  SendToStateRequest,
  ConfirmDestinationRequest,
  FilingDestinationConfirmedResponse,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiResponse, type ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

/**
 * Issue #861 — the treasurer "authorize and send" surface over the send-and-confirm backend. Three
 * hooks: the derived "Sent" + pre-authorize destination lens (`useFilingSendStatus`), the app-send
 * mutation (`useSendFilingToState`), and the one-time county-destination confirmation
 * (`useConfirmFilingDestination`). The report's own status never changes on send — "sent" is a lens
 * over the send record, so these mutations manage the send-status cache, not a report row/version.
 */

/**
 * A send / confirm failure that preserves the backend's stable error `code` (AlreadySent,
 * DestinationNotConfirmed, SendFailed, …) so the surface can branch to the right copy or step
 * (e.g. show the confirm-destination step on `DestinationNotConfirmed`) rather than parse a message.
 */
export class FilingSendActionError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'FilingSendActionError';
  }
}

// The error body can be the JSON literal `null`, hence the optional chaining.
function actionError(fallback: string) {
  return (body: ApiErrorBody | null, response: Response) =>
    new FilingSendActionError(
      (body?.code as string) ?? 'Unknown',
      (body?.error as string) ?? (body?.message as string) ?? fallback,
      response.status,
    );
}

async function throwFromResponse(response: Response, fallback: string): Promise<never> {
  const body = await response.json().catch(() => null);
  throw actionError(fallback)(body, response);
}

/**
 * The derived "Sent" lens for a report + the pre-authorize destination lens (resolved destination
 * verbatim, county-confirmation need). staleTime 0 so a fresh send, a delivery webhook, or a
 * destination confirmation shows on the authorize surface without a manual refresh.
 */
export function useFilingSendStatus(reportId: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.reports.sendStatus(reportId ?? ''),
    queryFn: () =>
      apiJson<FilingSendStatusDto>(`/api/finance/reports/${reportId}/send-status`, {
        error: 'Failed to fetch filing send status',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!reportId,
    staleTime: 0,
  });
}

/** The hook input: the report to send, plus the shared API body (`SendToStateRequest`). */
export type SendToStateVariables = { reportId: string } & SendToStateRequest;

/**
 * Email the report's generated filing to its state destination on the campaign's behalf. Follows the
 * optimistic-mutation cache lifecycle (cancel → snapshot → rollback → invalidate); there is no
 * concurrency token to write back because the report row is deliberately never mutated on send
 * ("sent" is a derived lens). The send-status query is invalidated on settle so the surface reflects
 * the new send (and, later, the delivery/bounce webhook).
 */
export function useSendFilingToState() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: ({ reportId, ...body }: SendToStateVariables): Promise<FilingSendResultDto> => {
      const payload: SendToStateRequest = body;
      return apiJson<FilingSendResultDto>(`/api/finance/reports/${reportId}/send-to-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: payload,
        error: actionError('Failed to send the filing'),
      });
    },
    onMutate: async ({ reportId }) => {
      const key = queryKeys.finance.reports.sendStatus(reportId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<FilingSendStatusDto>(key);
      return { key, previous };
    },
    onError: (_err, _vars, context) => {
      // Roll back to the pre-send snapshot so a failed send doesn't leave a stale optimistic lens.
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
    },
    onSettled: (_data, _err, { reportId }) => {
      // The send record changed (or a failure needs the real state back); refresh the lens and the
      // report detail (its delivery instructions / derived surfaces).
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.sendStatus(reportId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.detail(reportId) });
    },
  });

  return { sendToState: mutateAsync };
}

function readDispositionFileName(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star?.[1]) return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
  const plain = /filename=("?)([^";]+)\1/i.exec(disposition);
  if (plain?.[2]) return plain[2].trim();
  return fallback;
}

/**
 * Generate the SIGNED artifact and return its bytes for the download-and-file-it-yourself escape hatch
 * (issue #861), so the downloaded document carries the signature the treasurer drew. Posts the drawn
 * signature to the (non-finalizing) generate endpoint, which stamps + persists the signed package.
 */
export function useDownloadSignedFiling() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: async ({
      reportId,
      signatureImageBase64,
    }: {
      reportId: string;
      signatureImageBase64: string;
    }): Promise<{ fileBlob: Blob; fileName: string }> => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: { signatureImageBase64 },
      });
      if (!response.ok) await throwFromResponse(response, 'Failed to generate the signed filing');
      const fileBlob = await response.blob();
      const fileName = readDispositionFileName(
        response.headers.get('Content-Disposition'),
        `report-${reportId}-signed.pdf`,
      );
      return { fileBlob, fileName };
    },
    onSuccess: (_data, { reportId }) => {
      // A signed generate persists a new package; refresh the detail + send-status surfaces.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.detail(reportId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.sendStatus(reportId) });
    },
  });

  return { downloadSignedFiling: mutateAsync };
}

/** The hook input: the report, plus the shared API body (`ConfirmDestinationRequest`). */
export type ConfirmDestinationVariables = { reportId: string } & ConfirmDestinationRequest;

/**
 * Record the treasurer's one-time confirmation of a county filing destination (append-only decision).
 * On success the send-status lens is invalidated so `destinationConfirmed` flips and the authorize
 * step can proceed to the send.
 */
export function useConfirmFilingDestination() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: ({ reportId, destination }: ConfirmDestinationVariables) =>
      apiJson<FilingDestinationConfirmedResponse>(`/api/finance/reports/${reportId}/confirm-destination`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: { destination },
        error: actionError('Failed to confirm the filing destination'),
      }),
    onSettled: (_data, _err, { reportId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.sendStatus(reportId) });
    },
  });

  return { confirmDestination: mutateAsync };
}
